import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useDarkMode } from '../DarkModeContext';
import { useChatContext } from '../context/ChatContext';
import { MessageCircle, Send, X, User, Search, Circle, Minimize2, Paperclip, Download, FileText } from 'lucide-react';
import api from '../lib/api';

const FloatingPlatformChat = () => {
  const { darkMode } = useDarkMode();
  const {
    selectedCustomer: globalSelectedCustomer,
    messages: globalMessages,
    setSelectedCustomer: setGlobalSelectedCustomer,
    setMessages: setGlobalMessages,
    markAsRead,
    lastReadTimestamps,
    totalUnread
  } = useChatContext();

  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [guestSessions, setGuestSessions] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(globalSelectedCustomer);
  const [messages, setMessages] = useState(globalMessages);
  const [inputMessage, setInputMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeSessions, setActiveSessions] = useState({});
  const [unreadCounts, setUnreadCounts] = useState({});
  const [selectedFile, setSelectedFile] = useState(null);
  const [urlSessionId, setUrlSessionId] = useState(null); // Track if opened from URL
  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);

  // Listen for deep link events to open chat with specific session
  useEffect(() => {
    const handleOpenWithSession = (event) => {
      const { sessionId } = event.detail || {};
      console.log('[FloatingPlatformChat] Opening with sessionId:', sessionId);
      setIsOpen(true);
      setIsMinimized(false);

      if (sessionId) {
        // Create a guest session object to select
        if (sessionId.startsWith('chat_guest_')) {
          setSelectedCustomer({
            id: sessionId,
            sessionId: sessionId,
            userName: 'Guest Visitor',
            isGuest: true
          });
        }
      }
    };

    window.addEventListener('platformChat:openWithSession', handleOpenWithSession);
    return () => window.removeEventListener('platformChat:openWithSession', handleOpenWithSession);
  }, []);

  // Check URL for sessionId on mount and open chat automatically
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('sessionId');
    if (sessionId) {
      console.log('[FloatingPlatformChat] Found sessionId in URL:', sessionId);
      setUrlSessionId(sessionId);
      setIsOpen(true);
      setIsMinimized(false);

      // Create customer object based on session type
      const customerObj = sessionId.startsWith('chat_guest_') ? {
        id: sessionId,
        sessionId: sessionId,
        company_name: 'Guest Visitor',
        userName: 'Guest Visitor',
        user_first_name: 'Guest',
        user_last_name: '',
        isGuest: true
      } : {
        id: sessionId,
        sessionId: sessionId,
        company_name: 'Customer',
        isGuest: false
      };

      setSelectedCustomer(customerObj);
      setGlobalSelectedCustomer(customerObj);

      // Immediately fetch chat history for this session
      fetchChatHistoryDirect(sessionId);

      // Clean URL to remove sessionId parameter
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchCustomers();
      fetchActiveSessions();
      const interval = setInterval(fetchActiveSessions, 5000);
      return () => clearInterval(interval);
    }
  }, [isOpen]);

  useEffect(() => {
    if (selectedCustomer) {
      fetchChatHistory(selectedCustomer.id);
      const interval = setInterval(() => {
        fetchChatHistory(selectedCustomer.id);
      }, 2000);
      return () => clearInterval(interval);
    }
  }, [selectedCustomer]);

  useEffect(() => {
    if (isOpen) {
      calculateUnreadCounts();
      const interval = setInterval(calculateUnreadCounts, 5000);
      return () => clearInterval(interval);
    }
  }, [guestSessions, activeSessions, lastReadTimestamps, isOpen]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchCustomers = async () => {
    try {
      const response = await api.get('/api/platform/customers');
      setCustomers(response.data);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const fetchActiveSessions = async () => {
    try {
      const response = await api.get('/api/support/chat/active-sessions');
      const sessions = response.data.sessions || {};
      const guests = [];
      const customerSessions = {};

      Object.keys(sessions).forEach(userId => {
        if (userId === 'guest' || userId.startsWith('guest_') || userId.startsWith('chat_guest')) {
          guests.push({
            id: userId,
            sessionId: sessions[userId].sessionId,
            userName: sessions[userId].userName,
            userEmail: sessions[userId].userEmail,
            startedAt: sessions[userId].startedAt,
            isGuest: true
          });
        } else {
          customerSessions[userId] = sessions[userId];
        }
      });

      setGuestSessions(guests);
      setActiveSessions(customerSessions);
    } catch (error) {
      console.error('Error fetching active sessions:', error);
    }
  };

  // Direct fetch for URL-based session opening (called immediately on mount)
  const fetchChatHistoryDirect = async (sessionId) => {
    try {
      console.log('[FloatingPlatformChat] Direct fetch for sessionId:', sessionId);
      const response = await api.get(`/api/support/chat/history/${sessionId}`);
      const msgs = response.data.messages || [];
      console.log('[FloatingPlatformChat] Direct fetch got', msgs.length, 'messages');
      setMessages(msgs);
      setGlobalMessages(msgs);
      markAsRead(sessionId);
      setUnreadCounts(prev => ({ ...prev, [sessionId]: 0 }));
    } catch (error) {
      console.error('[FloatingPlatformChat] Error in direct fetch:', error);
    }
  };

  const fetchChatHistory = async (customerId) => {
    try {
      const response = await api.get(`/api/support/chat/history/${customerId}`);
      const msgs = response.data.messages || [];
      console.log('[FloatingPlatformChat] Fetched chat history:', {
        customerId,
        messageCount: msgs.length,
        messages: msgs.map(m => ({
          id: m.id,
          sender: m.sender,
          text: m.text?.substring(0, 30),
          attachmentCount: m.attachments?.length || 0,
          attachments: m.attachments
        }))
      });
      setMessages(msgs);
      setGlobalMessages(msgs);
      markAsRead(customerId);
      setUnreadCounts(prev => ({ ...prev, [customerId]: 0 }));
    } catch (error) {
      console.error('Error fetching chat history:', error);
    }
  };

  // Use batch endpoint instead of N+1 queries
  const calculateUnreadCounts = async () => {
    try {
      const lastReadKey = 'platformLastReadMessages';
      const lastReadMap = JSON.parse(localStorage.getItem(lastReadKey) || '{}');

      const response = await api.get('/api/support/chat/unread-counts', {
        params: { lastRead: JSON.stringify(lastReadMap) }
      });

      if (response.data.success) {
        const newUnreadCounts = {};
        const counts = response.data.counts || {};

        const allSessions = [
          ...guestSessions.map(g => ({ id: g.id, sessionId: g.sessionId })),
          ...Object.keys(activeSessions).map(userId => ({
            id: userId,
            sessionId: activeSessions[userId].sessionId
          }))
        ];

        for (const session of allSessions) {
          const sessionCount = counts[session.sessionId];
          if (sessionCount && sessionCount.unread > 0) {
            newUnreadCounts[session.id] = sessionCount.unread;
          }
        }

        setUnreadCounts(newUnreadCounts);
      }
    } catch (error) {
      console.error('Error calculating unread counts:', error);
    }
  };

  const handleSendMessage = async () => {
    if ((!inputMessage.trim() && !selectedFile) || !selectedCustomer) return;

    const messageText = inputMessage.trim() || (selectedFile ? `[Attachment: ${selectedFile.name}]` : '');
    const newMessage = {
      customerId: selectedCustomer.id,
      text: messageText,
      sender: 'support',
      timestamp: new Date(),
      attachments: selectedFile ? [{
        fileName: selectedFile.name,
        fileSize: selectedFile.size,
        mimeType: selectedFile.type,
        uploading: true
      }] : []
    };

    setMessages(prev => [...prev, newMessage]);
    const messageInput = inputMessage.trim();
    const fileToSend = selectedFile;
    setInputMessage('');
    setSelectedFile(null);

    try {
      if (fileToSend) {
        // Send message with attachment
        console.log('[FloatingPlatformChat] Sending message with attachment:', {
          fileName: fileToSend.name,
          fileSize: fileToSend.size,
          customerId: selectedCustomer.id,
          messageText: messageInput
        });
        const formData = new FormData();
        formData.append('customerId', selectedCustomer.id);
        formData.append('text', messageInput);
        formData.append('sender', 'support');
        formData.append('attachment', fileToSend);

        const response = await api.post('/api/support/chat/upload-attachment', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        console.log('[FloatingPlatformChat] Message with attachment sent successfully:', response.data);
      } else {
        // Regular text message
        await api.post('/api/support/chat/send', {
          customerId: selectedCustomer.id,
          text: messageInput,
          sender: 'support',
          timestamp: new Date()
        });
      }

      scrollToBottom();
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send message: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      // Check file size (10MB limit)
      if (file.size > 10 * 1024 * 1024) {
        alert('File size must be less than 10MB');
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDownloadAttachment = async (attachmentId, fileName) => {
    try {
      const response = await api.get(`/api/support/chat/download/${attachmentId}`, {
        responseType: 'blob'
      });

      // Create a download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('[FloatingPlatformChat] Error downloading attachment:', error);
      alert('Failed to download attachment');
    }
  };

  const handleStartChat = (customer) => {
    setSelectedCustomer(customer);
    setGlobalSelectedCustomer(customer);
    setIsMinimized(false);
  };

  const handleStartGuestChat = (guestSession) => {
    const customerObj = {
      id: guestSession.id,
      sessionId: guestSession.sessionId,
      company_name: 'Guest User',
      user_first_name: guestSession.userName,
      user_last_name: '',
      user_email: guestSession.userEmail || 'No email',
      isGuest: true
    };
    setSelectedCustomer(customerObj);
    setGlobalSelectedCustomer(customerObj);
    setIsMinimized(false);
  };

  const handleBackToList = () => {
    setSelectedCustomer(null);
    setGlobalSelectedCustomer(null);
  };

  const filteredCustomers = customers.filter(customer => {
    const searchLower = searchTerm.toLowerCase();
    return (
      customer.company_name?.toLowerCase().includes(searchLower) ||
      customer.user_email?.toLowerCase().includes(searchLower) ||
      `${customer.user_first_name} ${customer.user_last_name}`.toLowerCase().includes(searchLower)
    );
  });

  const chatContent = (
    <>
      {/* Floating Chat Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className={`fixed bottom-6 right-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 rounded-full p-4 ${
            darkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-600 hover:bg-blue-700'
          }`}
          style={{ zIndex: 9999 }}
          aria-label="Open customer chat"
        >
          <MessageCircle size={24} className="text-white" />
          {totalUnread > 0 && (
            <div className="absolute -top-1 -right-1 bg-red-600 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center animate-pulse">
              {totalUnread > 9 ? '9+' : totalUnread}
            </div>
          )}
        </button>
      )}

      {/* Floating Chat Window - Mobile optimized */}
      {isOpen && (
        <div
          className={`fixed
            /* Mobile: Full screen */
            inset-0 sm:inset-auto
            /* Desktop: Bottom right corner */
            sm:bottom-6 sm:right-6
            /* Width: Full on mobile, 384px on desktop */
            w-full sm:w-96
            /* Height: Full on mobile, 600px on desktop (unless minimized) */
            ${isMinimized ? 'h-14' : 'h-full sm:h-[600px]'}
            ${darkMode ? 'bg-gray-800' : 'bg-white'
          } sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden border-0 sm:border ${
            darkMode ? 'sm:border-gray-700' : 'sm:border-gray-200'
          }`}
          style={{ zIndex: 9999 }}
        >
          {/* Header */}
          <div className={`p-4 border-b ${darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-blue-600'} flex items-center justify-between`}>
            <div className="flex items-center gap-3">
              <MessageCircle size={20} className={darkMode ? 'text-blue-400' : 'text-white'} />
              <div>
                <h3 className={`font-semibold ${darkMode ? 'text-white' : 'text-white'}`}>
                  {selectedCustomer ? (
                    selectedCustomer.company_name || 'Chat'
                  ) : (
                    'Customer Support Chat'
                  )}
                </h3>
                {totalUnread > 0 && !selectedCustomer && (
                  <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-blue-100'}`}>
                    {totalUnread} unread message{totalUnread !== 1 ? 's' : ''}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setIsMinimized(!isMinimized)}
                className={`p-1.5 rounded hover:bg-white/20 transition-colors ${darkMode ? 'text-gray-400' : 'text-white'}`}
                aria-label={isMinimized ? 'Maximize' : 'Minimize'}
              >
                <Minimize2 size={18} />
              </button>
              <button
                onClick={() => {
                  setIsOpen(false);
                  setIsMinimized(false);
                }}
                className={`p-1.5 rounded hover:bg-white/20 transition-colors ${darkMode ? 'text-gray-400' : 'text-white'}`}
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>
          </div>

          {/* Content - Only show if not minimized */}
          {!isMinimized && (
            <>
              {selectedCustomer ? (
                // Chat View
                <>
                  <div className={`flex-1 overflow-y-auto p-4 space-y-4 ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
                    {/* Show different button text based on how chat was opened */}
                    <button
                      onClick={() => {
                        setUrlSessionId(null); // Clear URL session so user can navigate freely
                        handleBackToList();
                      }}
                      className={`text-sm ${darkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'} mb-2`}
                    >
                      {urlSessionId ? '← Show all chats' : '← Back to list'}
                    </button>
                    {messages.length === 0 ? (
                      <div className="text-center py-8">
                        <MessageCircle size={48} className={`mx-auto mb-3 ${darkMode ? 'text-gray-600' : 'text-gray-400'}`} />
                        <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          No messages yet. Start the conversation!
                        </p>
                      </div>
                    ) : (
                      messages.map((msg, index) => (
                        <div
                          key={index}
                          className={`flex ${msg.sender === 'support' ? 'justify-end' : 'justify-start'}`}
                        >
                          <div
                            className={`max-w-[70%] rounded-lg p-3 ${
                              msg.sender === 'support'
                                ? 'bg-blue-600 text-white'
                                : darkMode
                                ? 'bg-gray-800 text-gray-100'
                                : 'bg-white text-gray-800'
                            }`}
                          >
                            <p className="text-sm whitespace-pre-line">{msg.text}</p>
                            {msg.attachments && msg.attachments.length > 0 && (
                              <div className="mt-2 space-y-1">
                                {msg.attachments.map((attachment, attIndex) => (
                                  <div
                                    key={attIndex}
                                    className={`flex items-center gap-2 p-2 rounded ${
                                      msg.sender === 'support'
                                        ? 'bg-white/20'
                                        : darkMode
                                        ? 'bg-gray-700'
                                        : 'bg-gray-100'
                                    }`}
                                  >
                                    <FileText size={16} />
                                    <div className="flex-1 min-w-0">
                                      <p className="text-xs truncate">{attachment.fileName}</p>
                                      <p className="text-xs opacity-70">
                                        {(attachment.fileSize / 1024).toFixed(1)} KB
                                      </p>
                                    </div>
                                    {!attachment.uploading && attachment.id && (
                                      <button
                                        onClick={() => handleDownloadAttachment(attachment.id, attachment.fileName)}
                                        className={`p-1 rounded hover:bg-white/20 transition-colors`}
                                        title="Download"
                                      >
                                        <Download size={14} />
                                      </button>
                                    )}
                                    {attachment.uploading && (
                                      <span className="text-xs opacity-70">Uploading...</span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                            <p className="text-xs mt-1 opacity-70">
                              {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </p>
                          </div>
                        </div>
                      ))
                    )}
                    <div ref={messagesEndRef} />
                  </div>

                  {/* Input */}
                  <div className={`p-4 border-t ${darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}>
                    {selectedFile && (
                      <div className={`mb-2 p-2 rounded flex items-center justify-between ${
                        darkMode ? 'bg-gray-700' : 'bg-gray-100'
                      }`}>
                        <div className="flex items-center gap-2">
                          <FileText size={16} />
                          <div>
                            <p className="text-xs font-medium">{selectedFile.name}</p>
                            <p className="text-xs opacity-70">
                              {(selectedFile.size / 1024).toFixed(1)} KB
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={handleRemoveFile}
                          className={`p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors`}
                        >
                          <X size={16} />
                        </button>
                      </div>
                    )}
                    <div className="flex gap-2">
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileSelect}
                        className="hidden"
                        accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
                      />
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        className={`p-2 rounded-lg transition-colors ${
                          darkMode
                            ? 'bg-gray-700 hover:bg-gray-600 text-gray-200'
                            : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                        }`}
                        aria-label="Attach file"
                        title="Attach file (max 10MB)"
                      >
                        <Paperclip size={20} />
                      </button>
                      <input
                        type="text"
                        value={inputMessage}
                        onChange={(e) => setInputMessage(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                        placeholder="Type your message..."
                        className={`flex-1 px-4 py-2 rounded-lg border ${
                          darkMode
                            ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                            : 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-500'
                        } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      />
                      <button
                        onClick={handleSendMessage}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
                      >
                        <Send size={18} />
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                // Customer List View
                <>
                  {/* Search */}
                  <div className={`p-4 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                    <div className="relative">
                      <Search size={18} className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                      <input
                        type="text"
                        placeholder="Search customers..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className={`w-full pl-10 pr-4 py-2 rounded-lg border ${
                          darkMode
                            ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                            : 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-500'
                        } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      />
                    </div>
                  </div>

                  {/* Customer/Guest List */}
                  <div className="flex-1 overflow-y-auto">
                    {/* Guest Sessions */}
                    {guestSessions.length > 0 && (
                      <>
                        <div className={`px-4 py-2 text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-400 bg-gray-900' : 'text-gray-600 bg-gray-100'}`}>
                          Active Guests ({guestSessions.length})
                        </div>
                        {guestSessions.map((guestSession) => {
                          const unreadCount = unreadCounts[guestSession.id] || 0;
                          return (
                            <button
                              key={guestSession.id}
                              onClick={() => handleStartGuestChat(guestSession)}
                              className={`w-full p-3 border-b ${darkMode ? 'border-gray-700 hover:bg-gray-700' : 'border-gray-200 hover:bg-gray-100'} text-left transition-colors`}
                            >
                              <div className="flex items-center gap-3">
                                <div className={`relative w-10 h-10 rounded-full flex items-center justify-center ${darkMode ? 'bg-orange-900/30' : 'bg-orange-100'}`}>
                                  <User size={18} className={darkMode ? 'text-orange-400' : 'text-orange-600'} />
                                  {unreadCount > 0 && (
                                    <div className="absolute -top-1 -right-1 bg-red-600 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                                      {unreadCount > 9 ? '9+' : unreadCount}
                                    </div>
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className={`font-semibold text-sm truncate ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                    {guestSession.userName}
                                  </p>
                                  <p className={`text-xs truncate ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                                    Guest
                                  </p>
                                </div>
                                {unreadCount > 0 && (
                                  <Circle size={8} className="text-red-500 fill-current animate-pulse" />
                                )}
                              </div>
                            </button>
                          );
                        })}
                      </>
                    )}

                    {/* Registered Customers */}
                    {filteredCustomers.length > 0 && (
                      <>
                        <div className={`px-4 py-2 text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-400 bg-gray-900' : 'text-gray-600 bg-gray-100'}`}>
                          Customers
                        </div>
                        {filteredCustomers.map((customer) => {
                          const unreadCount = unreadCounts[customer.id] || 0;
                          const hasActiveSession = activeSessions[customer.id];
                          return (
                            <button
                              key={customer.id}
                              onClick={() => handleStartChat(customer)}
                              className={`w-full p-3 border-b ${darkMode ? 'border-gray-700 hover:bg-gray-700' : 'border-gray-200 hover:bg-gray-100'} text-left transition-colors`}
                            >
                              <div className="flex items-center gap-3">
                                <div className={`relative w-10 h-10 rounded-full flex items-center justify-center ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                                  <User size={18} className={darkMode ? 'text-gray-400' : 'text-gray-600'} />
                                  {unreadCount > 0 && (
                                    <div className="absolute -top-1 -right-1 bg-red-600 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                                      {unreadCount > 9 ? '9+' : unreadCount}
                                    </div>
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className={`font-semibold text-sm truncate ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                    {customer.company_name || 'No Company'}
                                  </p>
                                  <p className={`text-xs truncate ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                                    {customer.user_first_name} {customer.user_last_name}
                                  </p>
                                </div>
                                {hasActiveSession && (
                                  <Circle size={8} className="text-green-500 fill-current" />
                                )}
                              </div>
                            </button>
                          );
                        })}
                      </>
                    )}

                    {guestSessions.length === 0 && filteredCustomers.length === 0 && (
                      <div className="p-8 text-center">
                        <MessageCircle size={48} className={`mx-auto mb-3 ${darkMode ? 'text-gray-600' : 'text-gray-400'}`} />
                        <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          No active chats
                        </p>
                      </div>
                    )}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      )}
    </>
  );

  return ReactDOM.createPortal(chatContent, document.body);
};

export default FloatingPlatformChat;
